import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { getToken, removeToken } from '@utils/tokenUtils';
import { DEFAULT_LANGUAGE } from '@utils/languages';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    const { VITE_API_URL: URL, VITE_API_URL_DEV: URL_DEV } = import.meta.env;
    const baseURL = import.meta.env.MODE === 'development' ? URL_DEV : URL;

    this.api = axios.create({
      baseURL,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Accept-Language': DEFAULT_LANGUAGE,
        'Country-Code': '',
      },
    });

    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        if (config.url === 'import') {
          config.headers['Content-Type'] = 'multipart/form-data';
        }
        // Get token from localStorage or other storage
        const token = getToken();
        if (token) {
          config.headers.Authorization = token;
        }
        const savedCC = localStorage.getItem('savedCC');
        if (savedCC) {
          const {
            countryCodeData: { countryCode },
          } = JSON.parse(savedCC);
          config.headers['Country-Code'] = countryCode;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized access
          removeToken();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Change Accept-Language
  changeLanguage(lang: string) {
    this.api.defaults.headers['Accept-Language'] = lang;
  }

  // GET request
  get(url: string, config?: AxiosRequestConfig) {
    return this.api.get(url, config);
  }

  // POST request
  post(url: string, data: any, config?: AxiosRequestConfig) {
    return this.api.post(url, data, config);
  }

  // PUT request
  put(url: string, data: any, config?: AxiosRequestConfig) {
    return this.api.put(url, data, config);
  }

  // PATCH request
  patch(url: string, data: any, config?: AxiosRequestConfig) {
    return this.api.patch(url, data, config);
  }

  // DELETE request
  delete(url: string, config?: AxiosRequestConfig) {
    return this.api.delete(url, config);
  }
}

export const apiService = new ApiService();
